import { buttonBaseClasses, type Components, type ComponentsVariants, type CSSObject, svgIconClasses, type Theme } from '@mui/material'
import { darken } from '@mui/material/styles'

type VariantOverrideRecord<T extends string> = {
    [K in T]: true
}

type CybusButtonVariants = VariantOverrideRecord<
    'primary' | 'secondary' | 'primaryOutline' | 'primaryGhost' | 'ghost' | 'danger' | 'dangerOutline' | 'dangerGhost' | 'legacyAmber'
>

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides extends CybusButtonVariants {
        // Deprecates default MUI variants
        text: false
        contained: false
        outlined: false
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonOwnProps {
        /**
         * Unlike `Button`, `IconButton` has no `variant` prop on the MUI level at all,
         * but `IconButtonOwnProps` is exactly where extra props like this can be added.
         * So the `variant` prop is added here, on the MUI level, already mandatory,
         * meaning there's no need to also make it mandatory in our wrapper component (common/Button/IconButton.tsx).
         */
        variant: keyof CybusButtonVariants
    }
}

export const BUTTON_DISABLED_OPACITY = 0.4
export const ICON_BUTTON_MEDIUM_SIZE = 28

const FOCUS_RING_WIDTH = 2
const FOCUS_RING_GAP = 2
const ICON_BUTTON_FOCUS_RING_OFFSET = 1

/** The icon button's keyboard focus ring, shared with other controls that draw no focus style of their own, such as the pager's prev/next */
export const iconButtonFocusRing = (theme: Theme): CSSObject => ({
    outline: `${FOCUS_RING_WIDTH}px solid ${theme.palette.primary[500]}`,
    outlineOffset: ICON_BUTTON_FOCUS_RING_OFFSET,
})

const FOCUS_OUTLINE_WIDTH = 2

/** A keyboard focus outline drawn just inside the element's edge, for items that sit flush against their neighbours, such as table rows and navigation links */
export const insetFocusOutline = (theme: Theme): CSSObject => ({
    outline: `${FOCUS_OUTLINE_WIDTH}px solid ${theme.palette.primary[700]}`,
    outlineOffset: -FOCUS_OUTLINE_WIDTH,
})

export const secondaryButtonStyle = (theme: Theme): CSSObject => ({
    color: theme.palette.grey[700],
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.grey[300],
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
    },
    '&:active': {
        backgroundColor: theme.palette.grey[100],
        borderColor: theme.palette.grey[400],
    },
    '&:disabled': {
        color: theme.palette.grey[700],
    },
})

type VariantStyle = Readonly<{ props: { variant: keyof CybusButtonVariants }, style: (args: { theme: Theme }) => CSSObject }>

const variantStyles = [
    {
        props: { variant: 'primary' },
        style: ({ theme }) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary[700],
            '&:hover': {
                backgroundColor: theme.palette.primary[800],
            },
            '&:active': {
                backgroundColor: theme.palette.primary[900],
            },
            '&:disabled': {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary[700],
            },
        }),
    },
    {
        props: { variant: 'secondary' },
        style: ({ theme }) => secondaryButtonStyle(theme),
    },
    {
        props: { variant: 'primaryOutline' },
        style: ({ theme }) => ({
            color: theme.palette.primary[700],
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.primary[300],
            '&:hover': {
                backgroundColor: theme.palette.primary[100],
            },
            '&:active': {
                color: theme.palette.primary[800],
                backgroundColor: theme.palette.primary[200],
                borderColor: theme.palette.primary[400],
            },
            '&:disabled': {
                color: theme.palette.primary[700],
            },
        }),
    },
    {
        props: { variant: 'primaryGhost' },
        style: ({ theme }) => ({
            color: theme.palette.primary[700],
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
            },
            '&:active': {
                backgroundColor: theme.palette.grey[100],
            },
            '&:disabled': {
                color: theme.palette.primary[500],
            },
        }),
    },
    {
        props: { variant: 'ghost' },
        style: ({ theme }) => ({
            color: theme.palette.grey[600],
            '&:hover': {
                color: theme.palette.grey[700],
                backgroundColor: theme.palette.grey[100],
            },
            '&:active': {
                color: theme.palette.grey[800],
                backgroundColor: theme.palette.grey[200],
            },
            '&:disabled': {
                color: theme.palette.grey[600],
            },
        }),
    },
    {
        props: { variant: 'danger' },
        style: ({ theme }) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.error[600],
            '&:hover': {
                backgroundColor: theme.palette.error[700],
            },
            '&:active': {
                backgroundColor: theme.palette.error[800],
            },
            '&:disabled': {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.error[600],
            },
        }),
    },
    {
        props: { variant: 'dangerOutline' },
        style: ({ theme }) => ({
            color: theme.palette.error[700],
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.error[300],
            '&:hover': {
                backgroundColor: theme.palette.error[50],
                borderColor: theme.palette.error[400],
            },
            '&:active': {
                color: theme.palette.error[800],
                backgroundColor: theme.palette.error[100],
                borderColor: theme.palette.error[500],
            },
            '&:disabled': {
                color: theme.palette.error[700],
            },
        }),
    },
    {
        props: { variant: 'dangerGhost' },
        style: ({ theme }) => ({
            color: theme.palette.error[600],
            '&:hover': {
                color: theme.palette.error[700],
                backgroundColor: theme.palette.error[50],
            },
            '&:active': {
                color: theme.palette.error[800],
                backgroundColor: theme.palette.error[100],
            },
            '&:disabled': {
                color: theme.palette.error[600],
            },
        }),
    },
    /**
     * TODO: delete legacyAmber variant
     * @see https://cybusio.atlassian.net/browse/CC-4243
     */
    {
        props: { variant: 'legacyAmber' },
        style: ({ theme }) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
                backgroundColor: darken(theme.palette.secondary.main, 0.1),
            },
            '&:active': {
                backgroundColor: darken(theme.palette.secondary.main, 0.2),
            },
            '&:disabled': {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.secondary.main,
            },
        }),
    },
] satisfies VariantStyle[]

const ARIA_DISABLED = '&[aria-disabled="true"]'

/** An `aria-disabled` icon button keeps its focus and its tooltip; it looks disabled, keeping its rest colours on hover and press */
const withAriaDisabledRest = ({ props, style }: VariantStyle): VariantStyle => ({
    props,
    style: (args) => {
        const base = style(args)
        return {
            ...base,
            [`${ARIA_DISABLED}:hover, ${ARIA_DISABLED}:active`]: {
                color: base.color,
                backgroundColor: base.backgroundColor ?? 'transparent',
                borderColor: base.borderColor ?? 'transparent',
            },
        }
    },
})

const variants = <T extends 'MuiButton' | 'MuiIconButton'>(styles: readonly VariantStyle[]): ComponentsVariants<Theme>[T] =>
    styles as ComponentsVariants<Theme>[T]

export const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
    defaultProps: {
        // disables ripple effect for all buttons
        disableRipple: true,
    },
}

export const MuiButton: Components<Theme>['MuiButton'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.caption,
            letterSpacing: 0,
            fontWeight: 400,
            textTransform: 'none',
            minWidth: 'unset',
            border: '1px solid transparent',
            '&:disabled': { opacity: BUTTON_DISABLED_OPACITY },
            /** A gap in the page's background color, then the ring, so the ring never merges with the button's own border */
            [`&.${buttonBaseClasses.focusVisible}`]: {
                boxShadow: `0 0 0 ${FOCUS_RING_GAP}px ${theme.palette.background.default}, 0 0 0 ${FOCUS_RING_GAP + FOCUS_RING_WIDTH}px ${
                    theme.palette.primary[500]
                }`,
            },
        }),
        sizeSmall: ({ theme }) => ({
            minHeight: 24,
            padding: theme.spacing(0, 2),
            [`& .${svgIconClasses.root}`]: {
                height: 12,
                width: 12,
            },
        }),
        sizeMedium: ({ theme }) => ({
            minHeight: 28,
            padding: theme.spacing(0, 3),
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        }),
        sizeLarge: ({ theme }) => ({
            minHeight: 32,
            padding: theme.spacing(0, 4),
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        }),
    },
    variants: variants<'MuiButton'>(variantStyles),
}

export const MuiIconButton: Components<Theme>['MuiIconButton'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            border: '1px solid transparent',
            borderRadius: 4,
            padding: 0,
            [`&:disabled, ${ARIA_DISABLED}`]: { opacity: BUTTON_DISABLED_OPACITY },
            [ARIA_DISABLED]: { cursor: 'default' },
            [`&.${buttonBaseClasses.focusVisible}`]: iconButtonFocusRing(theme),
        }),
        sizeSmall: {
            height: 24,
            width: 24,
            [`& .${svgIconClasses.root}`]: {
                height: 12,
                width: 12,
            },
        },
        sizeMedium: {
            height: ICON_BUTTON_MEDIUM_SIZE,
            width: ICON_BUTTON_MEDIUM_SIZE,
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        },
        sizeLarge: {
            height: 32,
            width: 32,
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        },
    },
    variants: variants<'MuiIconButton'>(variantStyles.map(withAriaDisabledRest)),
}
